import { Language } from '@nxt-insurance/constants'

import { CALENDLY_STAGING_LINK } from '@flows/configs/shared/mgaLight/constants'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { SubFlow } from '@flows/insurance/types/subFlowTypes'

import type { PublicHealthStepParams } from './types'
import { successSchema } from './types'

const getCalendlyPrimaryLink = (params: PublicHealthStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/leben-gesundheit-pkv-2'
      : 'https://calendly.com/getsafe-leben-gesundheit/life-health-pkv-2'
  }

  return CALENDLY_STAGING_LINK
}

const getCalendlyAlternativeLink = (params: PublicHealthStepParams) => {
  if (params.environment === 'production') {
    return params.getCurrentLanguage() === Language.De
      ? 'https://calendly.com/getsafe-leben-gesundheit/kv-zusatz'
      : 'https://calendly.com/getsafe-leben-gesundheit/consultation-life-health-en-self'
  }

  return CALENDLY_STAGING_LINK
}

export const success = (params: PublicHealthStepParams) => {
  return {
    schema: successSchema,
    stepContent: [
      {
        type: 'animation',
        width: 140,
        height: 140,
        source: require('@ui-kit/assets/animations/check.json') as string,
      },
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.title',
        },
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.text',
        },
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.title',
        },
        subtitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.subtitle',
        },
        link: getCalendlyPrimaryLink(params),
        bgColor: 'grey100',
        condition: {
          type: 'conditions',
          comparisonType: 'anyOf',
          conditions: [
            {
              type: 'conditions',
              comparisonType: 'allOf',
              conditions: [
                {
                  type: 'conditions',
                  comparisonType: 'anyOf',
                  conditions: [
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment_status'),
                      expectedValue: 'permanently_employed',
                    },
                    {
                      type: 'value',
                      valuePath: params.helperFunctions.getParamPath('employment_status'),
                      expectedValue: 'other',
                    },
                  ],
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('income'),
                  expectedValue: 'more_than_74k',
                },
              ],
            },
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'self_employed',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'civil_servant',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'student',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'apprentice',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'retired',
                },
              ],
            },
          ],
        },
      },
      {
        type: 'calendly',
        title: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.title',
        },
        subtitle: {
          type: 'translatableText',
          translationKey: 'sharedFlows_common:steps.success.calendly.subtitle',
        },
        link: getCalendlyAlternativeLink(params),
        bgColor: 'grey100',
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'permanently_employed',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('employment_status'),
                  expectedValue: 'other',
                },
              ],
            },
            {
              type: 'conditions',
              comparisonType: 'anyOf',
              conditions: [
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('income'),
                  expectedValue: 'less_than_30k',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('income'),
                  expectedValue: '30k_to_50k',
                },
                {
                  type: 'value',
                  valuePath: params.helperFunctions.getParamPath('income'),
                  expectedValue: '50k_to_74k',
                },
              ],
            },
          ],
        },
      },
    ],
    subFlow: SubFlow.Common,
    onSubmit: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'updateConsultationRequest',
        inputs: [],
        outputs: [],
      },
    },
    hideBackButton: true,
    footer: { hideFooter: true },
    nextStep: 'appointment_details',
  } as const satisfies AnyStepConfiguration
}
